import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Menu, X } from 'lucide-react';
import { contactInfo } from '../mock';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      // If not on home page, navigate to home first, then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const handleAboutClick = () => {
    navigate('/about');
    setIsMenuOpen(false);
  };

  const handleReviewsClick = () => {
    navigate('/reviews');
    setIsMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold text-lg">
              AIS
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-slate-900">AIS Express</span>
              <span className="text-xs text-emerald-600 font-medium">Same Day Delivery</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('fleet')}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              Fleet
            </button>
            <button 
              onClick={handleAboutClick}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              About
            </button>
            <button 
              onClick={() => {
                navigate('/book');
                setIsMenuOpen(false);
              }}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              Book
            </button>
            <button 
              onClick={() => {
                navigate('/track');
                setIsMenuOpen(false);
              }}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              Track
            </button>
            <button 
              onClick={handleReviewsClick}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              Reviews
            </button>
            <button 
              onClick={() => {
                navigate('/faq');
                setIsMenuOpen(false);
              }}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
            >
              FAQ
            </button>
            <button 
              onClick={() => {
                navigate('/blog');
                setIsMenuOpen(false);
              }}
              className="text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              data-testid="header-blog-link"
            >
              Blog
            </button>
          </nav>

          {/* Call Button & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <Button 
              className="hidden sm:flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              onClick={() => window.open(`tel:${contactInfo.phone}`, '_self')}
            >
              <Phone className="h-4 w-4" />
              <span className="font-semibold">Call Now</span>
            </Button>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 bg-white">
            <nav className="flex flex-col space-y-4">
              <button 
                onClick={() => scrollToSection('home')}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('fleet')}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                Our Fleet
              </button>
              <button 
                onClick={handleAboutClick}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                About
              </button>
              <button 
                onClick={() => {
                  navigate('/book');
                  setIsMenuOpen(false);
                }}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                Book Delivery
              </button>
              <button 
                onClick={() => {
                  navigate('/track');
                  setIsMenuOpen(false);
                }}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                Track Order
              </button>
              <button 
                onClick={handleReviewsClick}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                Reviews
              </button>
              <button 
                onClick={() => {
                  navigate('/faq');
                  setIsMenuOpen(false);
                }}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                FAQ
              </button>
              <button 
                onClick={() => {
                  navigate('/blog');
                  setIsMenuOpen(false);
                }}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 font-medium transition-colors duration-200"
              >
                Blog
              </button>
              <Button 
                className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                onClick={() => window.open(`tel:${contactInfo.phone}`, '_self')}
              >
                <Phone className="h-4 w-4" />
                <span className="font-semibold">Call Now</span>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};